// Scenario & Policies — internal control to set the active persona context
// (scenario_run_id, run_id, experiments, period) that drives all downstream
// queries via URL params. Lives behind a gear button in the page header's
// actions slot and opens a right slide-over drawer, so it stays out of the way.
import { useState } from 'react';

import { Checkbox } from '@headlessui/react';
import { Cog6ToothIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Select, { type MultiValue } from 'react-select';

import type { SelectOption } from '@/components/filters/selectUtils';
import { policyName } from '@/features/decisions/config/personaContext';
import { MAX_COMPARISON_CANDIDATES } from '@/features/decisions/hooks/useComparisonCandidates';
import type { ExperimentLabel } from '@/features/decisions/scenarios.api.types';
import { parseExperimentIds } from '@/features/decisions/utils/fcfFormat';

interface ActivePersonaContextSettingsProps {
  scenarioRunId: string;
  runId: string;
  refExperimentId: string;
  experimentId: string;
  // The parsed selection, for the experiment_id picker — paired with `candidates` below.
  // Omitted (or `candidates` still loading) falls back to a plain text field over
  // `experimentId` so the setting stays editable either way.
  experimentIds?: string[];
  // This run's comparison candidates, for the experiment_id picker.
  candidates?: ExperimentLabel[];
  // Optional SKU x Site scope.
  sku?: string;
  site?: string;
  // Start and end of the chart/metric window. When provided, rendered as two
  // separate date fields.
  periodFrom?: string;
  periodTo?: string;
  // When true, the FCF chart shows a fixed value for Keystone Quantity Only instead of the API delta.
  keystoneOnlyOverride?: boolean;
  overrideValue?: number;
  // Multiplier applied to all OCF bar values in the chart.
  chartMultiplier?: number;
  onCommit: (key: string, value: string) => void;
}

export function ActivePersonaContextSettings({
  scenarioRunId,
  runId,
  refExperimentId,
  experimentId,
  experimentIds,
  candidates,
  sku,
  site,
  periodFrom,
  periodTo,
  keystoneOnlyOverride,
  overrideValue,
  chartMultiplier,
  onCommit,
}: ActivePersonaContextSettingsProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Scenario & Policies settings"
        title="Scenario & Policies (internal)"
        className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        data-testid="rep-active-context-toggle"
      >
        <Cog6ToothIcon className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" data-testid="rep-active-context">
          <div className="absolute inset-0 bg-black/30" aria-hidden onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                Scenario &amp; Policies
                <span className="ml-2 text-[10px] font-normal uppercase tracking-wide text-gray-400">Internal</span>
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <dl className="flex-1 space-y-2.5 overflow-y-auto p-4">
              <TextField label="scenario_run_id" paramKey="scenario_run_id" value={scenarioRunId} onCommit={onCommit} />
              <TextField label="run_id" paramKey="run_id" value={runId} onCommit={onCommit} />
              {sku !== undefined && <TextField label="sku" paramKey="sku" value={sku} onCommit={onCommit} />}
              {site !== undefined && <TextField label="site" paramKey="site" value={site} onCommit={onCommit} />}
              <TextField
                label="ref_experiment_id"
                paramKey="ref_experiment_id"
                value={refExperimentId}
                onCommit={onCommit}
                hint={`Incumbent baseline · ${policyName(refExperimentId, true)}`}
              />
              {candidates && experimentIds ? (
                <CandidatesField selectedIds={experimentIds} candidates={candidates} onCommit={onCommit} />
              ) : (
                <TextField
                  label="experiment_id"
                  paramKey="experiment_id"
                  value={experimentId}
                  onCommit={onCommit}
                  hint={`Comparison policies · ${parseExperimentIds(experimentId)
                    .map((id) => policyName(id))
                    .join(' · ')}`}
                />
              )}
              {keystoneOnlyOverride !== undefined && (
                <div className="flex items-start gap-3">
                  <dt className="w-40 shrink-0 pt-1 font-mono text-xs text-gray-500">
                    keystone_only_override
                    <div className="mt-0.5 break-words text-[10px] font-normal text-gray-400">
                      Show 1.1M fixed value for Keystone Quantity Only bar instead of API delta
                    </div>
                  </dt>
                  <dd className="flex-1 pt-1">
                    <Checkbox
                      checked={keystoneOnlyOverride}
                      onChange={(checked: boolean) => onCommit('keystone_only_override', checked ? 'true' : '')}
                      className="flex h-4 w-4 cursor-pointer items-center justify-center rounded border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 data-[checked]:border-blue-600 data-[checked]:bg-blue-600"
                    >
                      <svg
                        className="hidden h-3 w-3 text-white [[data-checked]_&]:block"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Checkbox>
                  </dd>
                </div>
              )}
              {keystoneOnlyOverride && overrideValue !== undefined && (
                <TextField
                  label="override_value"
                  paramKey="keystone_only_override_value"
                  value={String(overrideValue)}
                  onCommit={onCommit}
                  hint="Override value in dollars (e.g. 1100000)"
                />
              )}
              {chartMultiplier !== undefined && (
                <TextField
                  label="chart_multiplier"
                  paramKey="chart_multiplier"
                  value={String(chartMultiplier)}
                  onCommit={onCommit}
                  hint="Multiply all OCF bar values · clear field to reset to 1"
                />
              )}
              {periodFrom !== undefined && (
                <TextField
                  label="period_from"
                  paramKey="period_from"
                  value={periodFrom}
                  onCommit={onCommit}
                  hint="Start date (ISO — e.g. 2025-04-28)"
                />
              )}
              {periodTo !== undefined && (
                <TextField
                  label="period_to"
                  paramKey="period_to"
                  value={periodTo}
                  onCommit={onCommit}
                  hint="End date (ISO — e.g. 2025-05-12)"
                />
              )}
              {periodFrom !== undefined && periodTo !== undefined && periodFrom > periodTo && (
                <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <span className="font-medium">Invalid date range:</span> Start date must be on or before end date.
                </p>
              )}
            </dl>
          </div>
        </div>
      )}
    </>
  );
}

function TextField({
  label,
  paramKey,
  value,
  hint,
  onCommit,
}: {
  label: string;
  paramKey: string;
  value: string;
  hint?: string;
  onCommit: (key: string, value: string) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <dt className="w-40 shrink-0 pt-2 font-mono text-xs text-gray-500">
        {label}
        {hint && <div className="mt-0.5 break-words text-[10px] font-normal text-gray-400">{hint}</div>}
      </dt>
      <dd className="flex-1">
        <input
          key={value}
          type="text"
          data-testid={`rep-${paramKey}-input`}
          defaultValue={value}
          onBlur={(e) => onCommit(paramKey, e.target.value.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommit(paramKey, (e.target as HTMLInputElement).value.trim());
            }
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-mono text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </dd>
    </div>
  );
}

// Multi-select over this run's own candidates (excludes the position-1 reference/incumbent —
// that's ref_experiment_id's job), capped at MAX_COMPARISON_CANDIDATES the same way a Superset
// native filter caps a selection: once at the cap, remaining options are disabled rather than
// hidden, so it's clear why they can't be picked.
function CandidatesField({
  selectedIds,
  candidates,
  onCommit,
}: {
  selectedIds: string[];
  candidates: ExperimentLabel[];
  onCommit: (key: string, value: string) => void;
}) {
  const options: SelectOption[] = candidates.map((e) => ({
    value: e.experiment_id,
    label: e.experiment_label ?? e.experiment_id,
  }));

  // Keeps an id selectable/visible even if it isn't (yet, or no longer) in this run's
  // candidate list — e.g. this run's data is still loading, or the value came from a pasted
  // link for a different run.
  const selected: SelectOption[] = selectedIds.map(
    (id) => options.find((o) => o.value === id) ?? { value: id, label: policyName(id) },
  );

  const handleChange = (next: MultiValue<SelectOption>) => {
    onCommit(
      'experiment_id',
      next
        .slice(0, MAX_COMPARISON_CANDIDATES)
        .map((o) => o.value)
        .join(', '),
    );
  };

  return (
    <div className="flex items-start gap-3">
      <dt className="w-40 shrink-0 pt-2 font-mono text-xs text-gray-500">
        experiment_id
        <div className="mt-0.5 break-words text-[10px] font-normal text-gray-400">
          Comparison policies · up to {MAX_COMPARISON_CANDIDATES}
        </div>
      </dt>
      <dd className="flex-1">
        <Select<SelectOption, true>
          isMulti
          isClearable={false}
          inputId="rep-experiment_id-input"
          options={options}
          value={selected}
          onChange={handleChange}
          isOptionDisabled={() => selected.length >= MAX_COMPARISON_CANDIDATES}
          placeholder={options.length ? 'Select up to 5…' : 'Loading candidates…'}
          noOptionsMessage={() => 'No candidates for this run'}
          className="text-xs"
          styles={{
            control: (base, state) => ({
              ...base,
              minHeight: 32,
              fontSize: '0.75rem',
              borderColor: state.isFocused ? '#3b82f6' : base.borderColor,
              boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
            }),
            menu: (base) => ({ ...base, fontSize: '0.75rem', zIndex: 50 }),
          }}
        />
      </dd>
    </div>
  );
}
